/**
 * Shared key detector — chroma → Krumhansl-Schmuckler / Temperley profiles.
 *
 * Pure JS, AudioBuffer-in, no deps. Used by /bpm-key-finder and
 * /music-genre-detector.
 *
 *   var r = window.__detectKey(audioBuffer);
 *   // r = { key: 'C' | 'D' | … | 'B', mode: 'Major' | 'Minor',
 *   //       confidence: 5..99, chroma: number[12],
 *   //       altKey, altMode,      // runner-up — surface it when margin is thin
 *   //       margin,               // top-1 minus top-2 profile correlation
 *   //       tuningCents }         // measured detuning of the recording
 *
 * ── Why the chroma is a port of librosa's, not ours (rewritten 2026-08-04) ──
 *
 * Reported by BronzePidgeon (tool_comments #886): "Different results on the
 * same song in different audio formats." Measured on 18 real tracks × 13
 * variants: pure codec changes (wav/flac/mp3 320+128/m4a/ogg/opus) did NOT move
 * the answer, but converting the same song to mono flipped the key on 5 of 18,
 * and loudness-normalising it flipped 2 more. Both are things a format
 * converter does in passing, so from the user's chair it reads as "the format
 * changed the answer" — the report was right that something was wrong, just not
 * about what.
 *
 * The real defect was underneath: the detector barely tracked pitch at all.
 * Transposition test (resample a track up k semitones — a correct detector must
 * answer the same key shifted by k; chance ≈ 8 %):
 *
 *     the tool's inline chroma        20 %     ← what /bpm-key-finder shipped
 *     this module, previous version   32 %
 *     equal-weight semitone bands     17 %     ← tried, measured, rejected
 *     spectral-peak chroma            48 %     ← tried, measured, rejected
 *     librosa chroma_stft             92 %     ← the reference
 *     librosa chroma_cqt              94 %
 *     this file (port of the above)  100 %
 *
 * The cause of the 32 %: FFT bin spacing is LINEAR (10.8 Hz at 4096 / 44.1 kHz)
 * while semitones are logarithmic. At C2 a semitone spans 3.8 Hz — less than
 * one bin; at C6 it spans nine. Assigning each bin to its nearest pitch class
 * therefore gave every pitch class a different, frequency-dependent number of
 * bins, and that fixed imbalance did not average out: across 18 full tracks,
 * 9 came back as the same key regardless of the music.
 *
 * librosa's filter bank fixes exactly that, so this is a faithful port of
 * librosa.filters.chroma + librosa.feature.chroma_stft rather than a fourth
 * home-grown attempt:
 *   - Gaussian pitch-class bumps whose width follows the LOCAL bin spacing in
 *     chroma units, so an unresolvable low bin is spread instead of being
 *     slammed into one arbitrary pitch class.
 *   - Per-column L2 normalisation: every FFT bin carries the same total vote.
 *   - Octave weighting (Gaussian, centre 5 octaves above A0 ≈ 880 Hz, width 2
 *     octaves) fading out the unresolvable bottom and the harmonic-noise top.
 *   - Power spectrum, and per-frame max normalisation so one loud bar cannot
 *     outvote a whole quiet section.
 *
 * Agreement with librosa's own answer on 18 tracks went 6/18 → 13/18 exact
 * (16/18 share the tonic; the remaining gaps are parallel major/minor, the
 * known-hard half of key detection — which is why altKey exists).
 *
 * And the input is the channel AVERAGE, not channel 0: a mono copy of a song
 * must not be a different signal to the detector. That alone took cross-format
 * key instability from 6/18 tracks to 1/18 even before the chroma rewrite.
 *
 * Guarded by tests/playwright/key-detector-invariance.mjs (transposition
 * equivariance + stereo/mono agreement, both on a real track).
 */
(function(){
'use strict';

if (typeof window === 'undefined') return;

var KEY_NAMES = ['C','C#','D','D#','E','F','F#','G','G#','A','A#','B'];
var N_CHROMA = 12;
var CTROCT = 5.0, OCTWIDTH = 2.0;      // librosa.filters.chroma defaults

/* ── Cooley-Tukey FFT (in-place, radix-2) ── */
function fft(re, im){
    var n = re.length;
    for (var i = 1, j = 0; i < n; i++) {
        var bit = n >> 1;
        while (j & bit) { j ^= bit; bit >>= 1; }
        j ^= bit;
        if (i < j) {
            var t = re[i]; re[i] = re[j]; re[j] = t;
            t = im[i]; im[i] = im[j]; im[j] = t;
        }
    }
    for (var len = 2; len <= n; len *= 2) {
        var ang = -2 * Math.PI / len, wR = Math.cos(ang), wI = Math.sin(ang);
        for (var i = 0; i < n; i += len) {
            var cR = 1, cI = 0;
            for (var j = 0; j < len / 2; j++) {
                var h = len / 2;
                var tR = cR * re[i+j+h] - cI * im[i+j+h];
                var tI = cR * im[i+j+h] + cI * re[i+j+h];
                re[i+j+h] = re[i+j] - tR;
                im[i+j+h] = im[i+j] - tI;
                re[i+j] += tR;
                im[i+j] += tI;
                var nR = cR * wR - cI * wI; cI = cR * wI + cI * wR; cR = nR;
            }
        }
    }
}

/**
 * Port of librosa.filters.chroma(sr, n_fft, n_chroma=12, tuning, ctroct=5,
 * octwidth=2, norm=2, base_c=True). Returns [12][n_fft/2+1].
 */
function chromaFilterBank(sr, nFft, tuningSemitones){
    var nBins = (nFft >> 1) + 1;
    var A0 = 440 * Math.pow(2, tuningSemitones / 12) / 16;   // hz_to_octs reference
    var frq = new Float64Array(nFft);
    for (var k = 1; k < nFft; k++) frq[k] = N_CHROMA * Math.log2((k * sr / nFft) / A0);
    frq[0] = frq[1] - 1.5 * N_CHROMA;                        // librosa's DC stand-in
    var binw = new Float64Array(nFft);
    for (var k = 0; k < nFft - 1; k++) binw[k] = Math.max(frq[k + 1] - frq[k], 1.0);
    binw[nFft - 1] = 1;

    var wts = [];
    for (var c = 0; c < N_CHROMA; c++) wts.push(new Float64Array(nBins));
    var half = Math.round(N_CHROMA / 2);
    var col = new Float64Array(N_CHROMA);
    for (var k = 0; k < nBins; k++) {
        var colSq = 0;
        for (var c = 0; c < N_CHROMA; c++) {
            var d = frq[k] - c;
            d = ((d + half + 10 * N_CHROMA) % N_CHROMA) - half;
            var v = Math.exp(-0.5 * Math.pow(2 * d / binw[k], 2));
            col[c] = v; colSq += v * v;
        }
        var nrm = Math.sqrt(colSq) || 1;
        var oct = Math.exp(-0.5 * Math.pow((frq[k] / N_CHROMA - CTROCT) / OCTWIDTH, 2));
        for (var c = 0; c < N_CHROMA; c++) wts[c][k] = col[(c + 3) % N_CHROMA] / nrm * oct;
    }
    return wts;
}

/**
 * Detuning of the recording, in semitones (±0.5), as the circular mean of how
 * far spectral peaks sit from the equal-tempered grid. A vinyl rip running at
 * 442 Hz or a baroque ensemble at 415 Hz otherwise splits every note across two
 * adjacent pitch classes and smears the chroma.
 */
function estimateTuning(frames, sr, nFft){
    var sx = 0, sy = 0;
    var stride = Math.max(1, Math.floor(frames.length / 40));
    for (var f = 0; f < frames.length; f += stride) {
        var mag = frames[f], nb = mag.length, maxM = 0;
        for (var k = 0; k < nb; k++) if (mag[k] > maxM) maxM = mag[k];
        if (maxM <= 0) continue;
        var kLo = Math.max(1, Math.floor(100 * nFft / sr));
        var kHi = Math.min(nb - 2, Math.ceil(2000 * nFft / sr));
        for (var k = kLo; k <= kHi; k++) {
            if (mag[k] <= mag[k-1] || mag[k] < mag[k+1] || mag[k] < maxM * 0.02) continue;
            /* Parabolic interpolation on log magnitude — recovers a partial's
               frequency to a fraction of a bin. */
            var a = Math.log(mag[k-1] + 1e-12), b = Math.log(mag[k] + 1e-12), c = Math.log(mag[k+1] + 1e-12);
            var d = (a - c) / (2 * (a - 2 * b + c));
            if (!isFinite(d) || Math.abs(d) > 0.5) d = 0;
            var semi = 12 * Math.log2(((k + d) * sr / nFft) / 440);
            var dev = semi - Math.round(semi);
            sx += mag[k] * Math.cos(2 * Math.PI * dev);
            sy += mag[k] * Math.sin(2 * Math.PI * dev);
        }
    }
    if (!sx && !sy) return 0;
    return Math.atan2(sy, sx) / (2 * Math.PI);
}

function pearson(a, b){
    var n = a.length, sA = 0, sB = 0, sAB = 0, sA2 = 0, sB2 = 0;
    for (var i = 0; i < n; i++) {
        sA += a[i]; sB += b[i]; sAB += a[i] * b[i];
        sA2 += a[i] * a[i]; sB2 += b[i] * b[i];
    }
    var num = n * sAB - sA * sB;
    var den = Math.sqrt((n * sA2 - sA * sA) * (n * sB2 - sB * sB));
    return den === 0 ? 0 : num / den;
}

/* Profile ensemble — Krumhansl 1982 + Temperley 1999, equal weight. Each pair
   was calibrated on a different corpus, so averaging beats either alone on
   out-of-corpus material. (Albrecht-Shanahan 2013 was tried on both synthetic
   chord progressions and pitch-shifted real audio and showed no improvement.) */
var majProf  = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88];
var minProf  = [6.33, 2.68, 3.52, 5.38, 2.60, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];
var majProf2 = [0.748, 0.060, 0.488, 0.082, 0.670, 0.460, 0.096, 0.715, 0.104, 0.366, 0.057, 0.400];
var minProf2 = [0.712, 0.084, 0.474, 0.618, 0.049, 0.460, 0.105, 0.747, 0.404, 0.067, 0.133, 0.330];

function detectKey(buffer){
    if (!buffer || typeof buffer.getChannelData !== 'function') return null;
    var sr = buffer.sampleRate;
    var nch = buffer.numberOfChannels || 1;
    var len = buffer.length != null ? buffer.length : buffer.getChannelData(0).length;
    if (!(len > 0) || len < sr * 1.5) return null;

    /* Canonical analysis signal: every channel averaged. Reading channel 0
       alone made a mono export of a song a different input from the stereo
       original — one of the flips behind comment #886. */
    var data, i, c;
    if (nch > 1) {
        data = new Float32Array(len);
        for (c = 0; c < nch; c++) {
            var d = buffer.getChannelData(c);
            for (i = 0; i < len; i++) data[i] += d[i];
        }
        for (i = 0; i < len; i++) data[i] /= nch;
    } else {
        data = buffer.getChannelData(0);
    }

    // ~10.8 Hz bins at 44.1 kHz — the resolution librosa uses at 2048 / 22050.
    var nFft = 4096, hop = 2048;
    if (sr > 60000) { nFft = 8192; hop = 4096; }

    // Same coverage as before: up to 30 windows of 4 s at 50 % overlap ≈ 62 s.
    var segLen = Math.round(sr * 4), hopLen = Math.round(sr * 2);
    var numSegs = Math.min(30, Math.max(1, Math.floor((len - segLen) / hopLen) + 1));
    var end = Math.min(len, (numSegs - 1) * hopLen + segLen);

    var win = new Float32Array(nFft);
    for (i = 0; i < nFft; i++) win[i] = 0.5 * (1 - Math.cos(2 * Math.PI * i / (nFft - 1)));
    var nBins = (nFft >> 1) + 1;
    var frames = [];
    for (var pos = 0; pos + nFft <= end; pos += hop) {
        var re = new Float32Array(nFft), im = new Float32Array(nFft);
        for (i = 0; i < nFft; i++) re[i] = data[pos + i] * win[i];
        fft(re, im);
        var mag = new Float32Array(nBins);
        for (var k = 0; k < nBins; k++) mag[k] = Math.sqrt(re[k] * re[k] + im[k] * im[k]);
        frames.push(mag);
    }
    if (!frames.length) return null;

    var tuning = estimateTuning(frames, sr, nFft);
    var wts = chromaFilterBank(sr, nFft, tuning);

    var chroma = new Float64Array(12);
    var used = 0;
    for (var f = 0; f < frames.length; f++) {
        var m = frames[f], v = new Float64Array(12), mx = 0;
        for (var k2 = 0; k2 < nBins; k2++) {
            var p = m[k2] * m[k2];                            // power spectrum
            if (p === 0) continue;
            for (c = 0; c < 12; c++) v[c] += wts[c][k2] * p;
        }
        for (c = 0; c < 12; c++) if (v[c] > mx) mx = v[c];
        if (mx <= 0) continue;
        for (c = 0; c < 12; c++) chroma[c] += v[c] / mx;      // norm=inf, one vote per frame
        used++;
    }
    if (!used) return null;

    var maxC = 0;
    for (i = 0; i < 12; i++) if (chroma[i] > maxC) maxC = chroma[i];
    if (maxC <= 0) return null;
    for (i = 0; i < 12; i++) chroma[i] /= maxC;

    var scores = [];
    for (var shift = 0; shift < 12; shift++) {
        var rot = new Float64Array(12);
        for (i = 0; i < 12; i++) rot[i] = chroma[(i + shift) % 12];
        scores.push({ key: KEY_NAMES[shift], mode: 'Major', score: (pearson(rot, majProf) + pearson(rot, majProf2)) / 2 });
        scores.push({ key: KEY_NAMES[shift], mode: 'Minor', score: (pearson(rot, minProf) + pearson(rot, minProf2)) / 2 });
    }
    scores.sort(function(a, b){ return b.score - a.score; });
    var best = scores[0], second = scores[1];
    var margin = best.score - second.score;

    /* Confidence floor is 5, not 30: a 30-floor used to claim "at least 30 %
       sure" about answers that were a coin flip. Callers should surface altKey
       when this comes back low. */
    var conf = Math.min(99, Math.max(5, Math.round(50 + 50 * margin / (Math.abs(best.score) + 0.001))));

    var chromaArr = [];
    for (i = 0; i < 12; i++) chromaArr.push(chroma[i]);
    return {
        key: best.key, mode: best.mode, confidence: conf, chroma: chromaArr,
        altKey: second.key, altMode: second.mode,
        margin: margin, tuningCents: Math.round(tuning * 100),
    };
}

window.__detectKey = detectKey;

/* ── Streaming surface ────────────────────────────────────────────────────
 * detectKey() above is offline: it wants a whole AudioBuffer. A live lane
 * (microphone → AnalyserNode) has one FFT frame at a time and the same two
 * needs — turn a magnitude spectrum into a chroma, and score a chroma
 * against the key profiles.
 *
 * These are deliberately the SAME filter bank and the SAME profiles, not a
 * second implementation: the chroma in here is the librosa port that was
 * measured and rewritten after comment #886, and a hand-rolled "good enough"
 * live chroma would quietly be the 20 %-accurate one this module replaced.
 */
window.__chromaStream = {
    /* Filter bank for a given sample rate / FFT size. Build once, reuse per
       frame. `tuningSemitones` defaults to 0 (equal temperament): the offline
       path measures detuning across many frames, which a single live frame
       cannot do. */
    bank: function (sr, nFft, tuningSemitones) {
        return chromaFilterBank(sr, nFft, tuningSemitones || 0);
    },

    /* One magnitude spectrum → 12 chroma bins, max-normalised exactly like
       the offline loop (norm=inf, one vote per frame). `mag` is linear
       magnitude, not dB. Returns null for a silent frame so the caller can
       skip it instead of accumulating zeros. */
    frame: function (mag, wts, out) {
        var nBins = mag.length, c, k;
        var v = out || new Float64Array(12);
        for (c = 0; c < 12; c++) v[c] = 0;
        for (k = 0; k < nBins; k++) {
            var p = mag[k] * mag[k];
            if (p === 0) continue;
            for (c = 0; c < 12; c++) v[c] += wts[c][k] * p;
        }
        var mx = 0;
        for (c = 0; c < 12; c++) if (v[c] > mx) mx = v[c];
        if (mx <= 0) return null;
        for (c = 0; c < 12; c++) v[c] /= mx;
        return v;
    },

    /* Chroma (any scale) → best key, same profiles and confidence formula as
       detectKey(). Used by the live lane to name degrees once it has heard
       enough to guess a tonic. */
    key: function (chroma) {
        var i, mx = 0;
        var norm = new Float64Array(12);
        for (i = 0; i < 12; i++) if (chroma[i] > mx) mx = chroma[i];
        if (!(mx > 0)) return null;
        for (i = 0; i < 12; i++) norm[i] = chroma[i] / mx;

        var scores = [];
        for (var shift = 0; shift < 12; shift++) {
            var rot = new Float64Array(12);
            for (i = 0; i < 12; i++) rot[i] = norm[(i + shift) % 12];
            scores.push({ tonic: shift, mode: 'major', score: (pearson(rot, majProf) + pearson(rot, majProf2)) / 2 });
            scores.push({ tonic: shift, mode: 'minor', score: (pearson(rot, minProf) + pearson(rot, minProf2)) / 2 });
        }
        scores.sort(function (a, b) { return b.score - a.score; });
        var best = scores[0], margin = best.score - scores[1].score;
        return {
            tonic: best.tonic,
            mode: best.mode,
            confidence: Math.min(99, Math.max(5, Math.round(50 + 50 * margin / (Math.abs(best.score) + 0.001)))),
        };
    }
};

})();
